// Quote Builder — Place Order flow.
//
// Mirrors the legacy `_qbv3PlaceOrder` / `_qbv3ConfirmOrders` modal flow at
// kos-qb-v3.js:5536-5610. The modal groups items by supplier and shows
// trade totals (NEVER customer-visible). Confirming flips status to
// 'ordered' and dispatches a `qb:order-placed` CustomEvent on window so
// downstream wiring (kitchen-os audit-log endpoint, when wired) can
// observe it.
//
// DEFRAUD SAFEGUARDS:
//   • Quote MUST have a reference (placeholder OK; server normalises).
//   • Quote MUST have ≥ 1 valid line item with qty > 0.
//   • Status transition is logged via the audit event.
//   • No Stripe touch — payment is a separate customer-portal flow.

import { iconMarkup } from "@tkos/shared-ui";
import type { QuoteStore } from "./state";
import type { PageContext } from "../../shell/types";
import type { QuoteItem } from "./types";
import { SUPPLIERS } from "./types";
import { escapeHtml, fmtPence, pencify } from "./formatters";
import { pdfSpecLines } from "./pdf";

export function openPlaceOrder(store: QuoteStore, ctx: PageContext): void {
  const q = store.get();
  if (!q.reference) {
    ctx.showToast("Quote has no reference — save first", "error");
    return;
  }

  // Group valid line items by supplier.
  const groups = new Map<string, { items: Array<{ item: QuoteItem; zoneName: string }>; tradePence: number }>();
  for (const z of q.zones) {
    for (const it of z.items) {
      if (!(it.desc || it.code) || (it.qty || 0) <= 0) continue;
      const key = it.supplier || (it.code?.split("-")[0]) || "Manual";
      const slot = groups.get(key) ?? { items: [], tradePence: 0 };
      slot.items.push({ item: it, zoneName: z.name });
      slot.tradePence += pencify(it.tradePrice || 0) * (it.qty || 0);
      groups.set(key, slot);
    }
  }

  if (groups.size === 0) {
    ctx.showToast("Add at least one line item before placing an order", "error");
    return;
  }

  const totalTradePence = [...groups.values()].reduce((s, g) => s + g.tradePence, 0);
  const supColour = (k: string): string =>
    SUPPLIERS.find((s) => s.supplierKey === k || s.key === k)?.colour ?? "#71717a";

  const overlay = document.createElement("div");
  overlay.className = "qb-modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.innerHTML = `
    <div class="qb-modal qb-modal--lg">
      <header class="qb-modal__head">
        <h3>Place order — trade prices</h3>
        <button type="button" class="qb-modal__close" data-role="close" aria-label="Close">${iconMarkup("close", { size: "sm" })}</button>
      </header>
      <div class="qb-modal__warning">Trade prices shown — never visible to customers</div>
      <div class="qb-modal__body">
        <p>Orders will be sent to <strong>${groups.size}</strong> supplier${groups.size === 1 ? "" : "s"}${q.customer ? ` for <strong>${escapeHtml(q.customer)}</strong>` : ""}. Generate a purchase order per supplier — raise them as you place each one.</p>
        <div class="qb-po-delivery">
          <label class="qb-po-delivery__field">
            <span>Delivery date</span>
            <input type="date" data-role="po-delivery-date" />
          </label>
          <label class="qb-po-delivery__field qb-po-delivery__to">
            <span>Deliver to</span>
            <select data-role="po-deliver-select"><option value="">Loading addresses…</option></select>
          </label>
        </div>
        <div class="qb-po-delivery__manual" data-role="po-manual" hidden>
          <input type="text" data-mf="label" placeholder="Label (e.g. Site office)" />
          <input type="text" data-mf="line1" placeholder="Address line 1" />
          <input type="text" data-mf="line2" placeholder="Address line 2 (optional)" />
          <input type="text" data-mf="city" placeholder="Town / city" />
          <input type="text" data-mf="postcode" placeholder="Postcode" />
        </div>
        ${[...groups.entries()].map(([sup, g]) => `
          <section class="qb-po-block" data-supplier="${escapeHtml(sup)}">
            <header class="qb-po-block__head">
              <span class="qb-po-block__name">
                <span class="qb-po-block__dot" style="--qb-supplier-colour:${supColour(sup)}"></span>
                ${escapeHtml(sup === "Manual" ? "Manual / custom items" : sup)}
              </span>
              <span class="qb-po-block__meta">${g.items.reduce((s, x) => s + (x.item.qty || 0), 0)} item${g.items.reduce((s, x) => s + (x.item.qty || 0), 0) === 1 ? "" : "s"} · <strong>${fmtPence(g.tradePence)}</strong></span>
            </header>
            <ul class="qb-po-block__items">
              ${g.items.map((x) => `
                <li>
                  <div class="qb-po-item__main">
                    <div class="qb-po-item__name">${escapeHtml(x.item.desc || x.item.code || "Item")}</div>
                    <div class="qb-po-item__sub">${escapeHtml(x.item.code || "")} · zone: ${escapeHtml(x.zoneName)}${x.item.notes ? ` · Note: ${escapeHtml(x.item.notes)}` : ""}</div>
                  </div>
                  <div class="qb-po-item__right">
                    Qty ${x.item.qty}
                    <div class="qb-po-item__price">${fmtPence(pencify(x.item.tradePrice || 0) * (x.item.qty || 0))}</div>
                  </div>
                </li>
              `).join("")}
            </ul>
            <div class="qb-po-block__action" data-po-action="${escapeHtml(sup)}">
              <button type="button" class="qb-btn qb-btn--ghost qb-po-gen" data-role="gen-po" data-supplier="${escapeHtml(sup)}">Generate PO</button>
            </div>
          </section>
        `).join("")}
      </div>
      <footer class="qb-modal__foot">
        <span class="qb-po-total">Trade total: <strong>${fmtPence(totalTradePence)}</strong></span>
        <div class="qb-modal__foot-actions">
          <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Cancel</button>
          <button type="button" class="qb-btn qb-btn--primary" data-role="confirm">Confirm &amp; send orders</button>
        </div>
      </footer>
    </div>
  `;

  const close = (): void => overlay.remove();
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='confirm']")?.addEventListener("click", () => {
    store.set((d) => { d.status = "ordered"; });
    close();
    ctx.showToast("Orders dispatched · status set to Ordered", "success");

    const supplierPayload = [...groups.entries()].map(([name, g]) => ({
      name,
      lineCount: g.items.length,
      tradePence: g.tradePence,
    }));

    // Persist to portal_orders so the dashboard pipeline, operations
    // tile, and the supplier portal's read-only orders view all see
    // this transition. Server emits `order.updated` after the DB
    // commit; the dashboard subscribes via the WebSocket channel.
    // No Stripe interaction — customer payments still go through
    // `_qbv3SendCustomer` → `/api/quotes/send` → customer portal.
    fetch("/api/portal/orders", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        quoteId:         q.serverId || null,
        quoteReference:  q.reference,
        customerId:      q.customerId || null,
        suppliers:       supplierPayload,
        totalTradePence,
        status:          "placed",
      }),
    }).catch((err) => {
      console.warn("[qb-place-order] /api/portal/orders POST failed:", err);
    });

    // Defraud / audit: emit a CustomEvent so the chrome (or a future
    // /api/audit-log POST hook) can record this transition. We do NOT
    // initiate Stripe here — supplier orders are separate from customer
    // payment. Customer payments go through `_qbv3SendCustomer` →
    // `/api/quotes/send` → customer portal → Stripe.
    try {
      window.dispatchEvent(new CustomEvent("qb:order-placed", {
        detail: {
          quoteId: q.id,
          reference: q.reference,
          customer: q.customer,
          totalTradePence,
          suppliers: [...groups.keys()],
          at: Date.now(),
        },
      }));
      // TODO(orchestrator): once /api/audit-log lands, POST:
      //   { action: 'quote.ordered', entity_type: 'quote', entity_id: q.id,
      //     new_value: { reference, status: 'ordered', suppliers, totalTradePence } }
    } catch (err) {
      console.warn("[qb-place-order] audit event dispatch failed:", err);
    }
  });

  // ── Per-supplier PO generation ───────────────────────────────────────────
  const allSuppliers = [...groups.keys()];
  const actionSlot = (supplier: string): HTMLElement | null =>
    [...overlay.querySelectorAll<HTMLElement>("[data-po-action]")].find((el) => el.dataset.poAction === supplier) ?? null;
  const markGenerated = (supplier: string, poNumber: string, poId: string): void => {
    const slot = actionSlot(supplier);
    if (!slot) return;
    slot.innerHTML = `<span class="qb-po-gen__done">✓ ${escapeHtml(poNumber)}</span>${poId
      ? ` <a class="qb-po-gen__pdf" href="/api/portal/purchase-orders/${encodeURIComponent(poId)}/pdf" target="_blank" rel="noopener">Download PDF</a>`
      : ""}`;
  };
  const getDeliveryDate = (): string =>
    overlay.querySelector<HTMLInputElement>("[data-role='po-delivery-date']")?.value || "";

  // Delivery target dropdown: customer address + saved retailer addresses +
  // one-off manual entry. Defaults to the primary saved address.
  interface SavedAddr { id: string; label: string; line1: string; line2: string; city: string; county: string; postcode: string; country: string; isPrimary: boolean }
  let savedAddresses: SavedAddr[] = [];
  let customerAddress = "";
  const selectEl = overlay.querySelector<HTMLSelectElement>("[data-role='po-deliver-select']")!;
  const manualEl = overlay.querySelector<HTMLElement>("[data-role='po-manual']")!;
  const syncManual = (): void => { manualEl.hidden = selectEl.value !== "oneoff"; };
  const rebuildSelect = (): void => {
    const opts: string[] = [];
    if (customerAddress) opts.push(`<option value="customer">Customer's address</option>`);
    for (const a of savedAddresses) {
      opts.push(`<option value="saved:${escapeHtml(a.id)}"${a.isPrimary ? " selected" : ""}>${escapeHtml(a.label || "Saved address")}</option>`);
    }
    opts.push(`<option value="oneoff">Enter a one-off address…</option>`);
    selectEl.innerHTML = opts.join("");
    syncManual();
  };
  selectEl.addEventListener("change", syncManual);

  const deliveryChoice = (): { deliveryTo: string; deliveryAddress: unknown } => {
    const v = selectEl.value;
    if (v === "customer") return { deliveryTo: "customer", deliveryAddress: customerAddress };
    if (v === "oneoff") {
      const mf = (k: string): string => manualEl.querySelector<HTMLInputElement>(`[data-mf='${k}']`)?.value.trim() || "";
      return { deliveryTo: "retailer", deliveryAddress: { label: mf("label") || "One-off address", line1: mf("line1"), line2: mf("line2"), city: mf("city"), postcode: mf("postcode") } };
    }
    if (v.startsWith("saved:")) {
      const a = savedAddresses.find((x) => x.id === v.slice(6));
      if (a) return { deliveryTo: "retailer", deliveryAddress: a };
    }
    return { deliveryTo: "retailer", deliveryAddress: undefined };
  };

  // Resolve the customer's address + saved retailer addresses, then build the
  // dropdown (default = primary saved address).
  void (async () => {
    try {
      if (q.customerId) {
        const res = await ctx.api<{ contacts?: Array<{ id?: string; address?: string }> }>("/crm/contacts");
        const c = res.contacts?.find((x) => x.id === q.customerId);
        if (c && typeof c.address === "string") customerAddress = c.address;
      }
    } catch { /* best-effort */ }
    try {
      const res = await ctx.api<{ addresses?: SavedAddr[] }>("/portal/settings/delivery-addresses");
      if (Array.isArray(res.addresses)) savedAddresses = res.addresses;
    } catch { /* best-effort */ }
    rebuildSelect();
  })();

  // Reflect any POs already raised for this job (so re-opening shows them done).
  void (async () => {
    try {
      const res = await ctx.api<{ purchaseOrders?: Array<{ supplier?: string; poNumber?: string; id?: string }> }>(
        `/portal/purchase-orders?quoteRef=${encodeURIComponent(q.reference)}`,
      );
      for (const po of res.purchaseOrders ?? []) {
        if (po.supplier) markGenerated(po.supplier, po.poNumber || "", po.id || "");
      }
    } catch { /* best-effort */ }
  })();

  overlay.querySelectorAll<HTMLButtonElement>("[data-role='gen-po']").forEach((btn) => {
    btn.addEventListener("click", () => {
      const sup = btn.dataset.supplier || "";
      const g = groups.get(sup);
      if (!g) return;
      const choice = deliveryChoice();
      btn.disabled = true;
      btn.textContent = "Generating…";
      // `spec` and `notes` carry the MANUFACTURING choices to the supplier.
      //
      // Until 2026-09-07 this sent five fields and nothing else, so hinge
      // side, edging colour, reduced gable, blanking panel side and any
      // cut-to-size dimension reached the screen and the internal PDF but
      // never the purchase order. The description was the only thing a
      // supplier ever saw.
      //
      // `spec` comes from pdfSpecLines — the SAME function the internal PDF
      // uses — rather than a second implementation here, so the two documents
      // cannot come to disagree about what was ordered.
      const lineItems = g.items.map((x) => ({
        code: x.item.code || "",
        description: x.item.desc || x.item.code || "Item",
        qty: x.item.qty || 0,
        tradePence: pencify(x.item.tradePrice || 0),
        zone: x.zoneName,
        spec: pdfSpecLines(x.item).join(" · "),
        notes: x.item.notes || "",
      }));
      void (async () => {
        try {
          const res = await ctx.api<{ ok?: boolean; poNumber?: string; poId?: string }>("/portal/purchase-orders", {
            method: "POST",
            body: JSON.stringify({
              quoteId: q.serverId || null,
              quoteReference: q.reference,
              customerId: q.customerId || null,
              customerName: q.customer,
              supplier: sup,
              lineItems,
              deliveryTo: choice.deliveryTo,
              deliveryAddress: choice.deliveryAddress,
              deliveryDate: getDeliveryDate() || undefined,
              allSuppliers,
            }),
          });
          if (!res.ok || !res.poNumber) throw new Error("po failed");
          ctx.showToast(`PO ${res.poNumber} created`, "success");
          markGenerated(sup, res.poNumber, res.poId || "");
        } catch {
          btn.disabled = false;
          btn.textContent = "Generate PO";
          ctx.showToast("Couldn't generate the PO", "error");
        }
      })();
    });
  });

  document.body.appendChild(overlay);
}
